import { useCallback } from 'react';
import useIsMobile from 'app/hooks/useIsMobile';
import { useLocation, useNavigate } from 'react-router-dom';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import { getConfig } from 'app/services/config';
import { ModuleItemActionType } from 'app/services/helpCenter';
import { useContactFormState } from 'app/modules/contact-form/stores/useContactFormState';
import { getContactFormSlugFromUrl, isContactFormURL } from 'app/utils/contactFormUrl';

import useShouldAutoOpen from './useShouldAutoOpen';
import usePersistedSearch from './usePersistedSearch';
import { usePersistedLocation } from './usePersistedLocation';
import usePdfViewerEnabled from './usePdfViewerEnabled';
import useInterstitialPage from './useInterstitialPage';
import { useSearch } from './useSearch';

export function isInternalLink(url: string): boolean {
  if (!url) {
    return false;
  }

  if (url.startsWith('/')) {
    return true;
  }

  const urlObj = new URL(url);
  return urlObj.hostname === window.location.hostname;
}

function isContactURL(url: string): boolean {
  return /^(tel|mailto):/gim.test(url);
}

export function checkIsPdf(url: string) {
  const urlObj = new URL(url, window.location.origin);
  return urlObj.pathname.includes('.pdf');
}

function isContactFormAction(action?: string) {
  return action === ModuleItemActionType.OPEN_CONTACT_FORM;
}

export function shouldShowInterstitial(url: string, interstitialEnabled: boolean): boolean {
  const urlParams = new URLSearchParams(new URL(url, window.location.origin).search);
  return !isContactURL(url) && !urlParams.has('bypassInterstitial') && !isInternalLink(url) && interstitialEnabled;
}

function useLinks() {
  const { data: helpCenter } = useFetchHelpCenter();
  const isMobile = useIsMobile();
  const shouldAutoOpen = !isMobile;
  const location = useLocation();
  const navigate = useNavigate();
  const [, setShouldAutoOpen] = useShouldAutoOpen();
  const [, setPersistedSearch] = usePersistedSearch();
  const [, setPersistedLocation] = usePersistedLocation();
  const { search } = useSearch();
  const urlConfig = helpCenter?.external_url_param_config;
  const pdfViewerEnabled = usePdfViewerEnabled();
  const { openContactForm } = useContactFormState();
  const { isInterstitialEnabledForURL } = useInterstitialPage();

  interface LinkDetails {
    type?: 'resource' | 'external';
    action_type?: ModuleItemActionType;
  }

  const shouldOpenInNewTab = useCallback(
    (url: string, linkDetails?: LinkDetails): boolean => {
      const isPdf = checkIsPdf(url);
      const isResource = linkDetails?.type === 'resource';
      const isExternal = !isInternalLink(url);

      return isMobile || isPdf || isResource || isExternal;
    },
    [isMobile]
  );

  const addExternalParam = useCallback(
    (url: URL) => {
      if (urlConfig?.enabled) {
        url.searchParams.set(urlConfig.name, urlConfig.value);
      }

      return url;
    },
    [urlConfig]
  );

  const openLink = useCallback(
    (url: string, linkDetails?: LinkDetails) => {
      const urlObject = addExternalParam(new URL(url));

      if (isMobile || isInternalLink(url)) {
        setPersistedSearch(search);
        setPersistedLocation(location.pathname);
      }

      if (isContactFormURL(url) || isContactFormAction(linkDetails?.action_type)) {
        return openContactForm(getContactFormSlugFromUrl(url));
      }

      setShouldAutoOpen(shouldAutoOpen);

      const interstitialEnabled = isInterstitialEnabledForURL(url);

      if (shouldShowInterstitial(url, interstitialEnabled)) {
        return navigate('/tailor/interstitial', {
          state: { destination: urlObject.toString() },
        });
      }

      if (isContactURL(url)) {
        return window.open(url, '_self');
      }

      urlObject.searchParams.delete('bypassInterstitial');

      if (checkIsPdf(url) && pdfViewerEnabled) {
        const pdfHostname = getConfig('APP_URL');

        const pdfUrl = `${pdfHostname}/pdf.html?url=${urlObject}`;
        return window.open(pdfUrl, shouldOpenInNewTab(url, linkDetails) ? '_blank' : '_self');
      }

      return window.open(urlObject.toString(), shouldOpenInNewTab(url, linkDetails) ? '_blank' : '_self');
    },
    [
      isMobile,
      shouldAutoOpen,
      search,
      location.pathname,
      setPersistedSearch,
      setPersistedLocation,
      setShouldAutoOpen,
      navigate,
      shouldOpenInNewTab,
      pdfViewerEnabled,
      openContactForm,
      addExternalParam,
      isInterstitialEnabledForURL,
    ]
  );

  const getLinkTarget = useCallback(
    (url: string): string => {
      return shouldOpenInNewTab(url) ? '_blank' : '_self';
    },
    [shouldOpenInNewTab]
  );

  const showInterstitialForURL = useCallback(
    (url: string) => {
      const enabledForSpecificURL = isInterstitialEnabledForURL(url);
      const enabledForGeneralURL = !!helpCenter?.interstitial_page?.display_interstitial;

      return enabledForSpecificURL || shouldShowInterstitial(url, enabledForGeneralURL);
    },
    [isInterstitialEnabledForURL, helpCenter]
  );

  return {
    openLink,
    getLinkTarget,
    checkIsPdf,
    shouldShowInterstitial: showInterstitialForURL,
  };
}

export default useLinks;
